import {
  action,
  AnnotationsMap,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import documentsBackend, {
  CreateTemporaryDocumentResponse,
  DefaultValueInResponse,
  GetOverridableTemplatePlaceholdersResponse,
} from '@lexian/api/documents-api';
import asyncBackend, {
  TemporaryDocumentCreatedPushNotification,
  TemporaryDocumentPdfCreatedPushNotification,
} from '@lexian/api/notifications-async-backend';
import {State, unexpectedError} from '_/utilities';
import {
  assertUnreachable,
  Loadable,
  LoggedInUserInformation,
} from '_/utilities/types';

import {
  LabeledTemplatePlaceholderWizardStep,
  Notification,
  PlaceholderPage,
  TemplatePlaceholderWizardStep,
  TemplateWizardStep,
} from './template.types';
import {composeTemplateWizardSteps} from './template.utils';

const baseStoreMobXAnnotations: AnnotationsMap<
  TemplateBaseStore,
  'markTemporaryDocumentAsReady'
> = {
  temporaryDocumentId: observable,
  temporaryDocumentDataState: observable,
  placeholderLoader: observable,
  wizardSteps: observable,
  wizardStep: observable,
  notification: observable,
  unsubscribe: action.bound,
  stepForward: action.bound,
  stepBack: action.bound,
  markTemporaryDocumentAsReady: action.bound,
};

export default abstract class TemplateBaseStore {
  protected receivedTemporaryDocumentIds: string[] = [];
  public temporaryDocumentId?: string;
  public temporaryDocumentDataState: State = State.Loading;

  private readonly includeRecipientStep: boolean;

  public placeholderLoader: Loadable<PlaceholderPage[]>;
  public wizardSteps: LabeledTemplatePlaceholderWizardStep[] = [];
  public wizardStep: TemplatePlaceholderWizardStep;

  public notification?: Notification;
  protected unsubscribeWebsocket?: () => void;

  public constructor({
    loggedInUser,
    includeRecipientStep = false,
  }: {
    loggedInUser: LoggedInUserInformation;
    includeRecipientStep?: boolean;
  }) {
    makeObservable(this, baseStoreMobXAnnotations);

    this.includeRecipientStep = includeRecipientStep;

    this.placeholderLoader = {state: State.Success};
    this.composeWizardSteps();
    this.wizardStep = TemplateWizardStep.ChooseTemplate;

    asyncBackend.initializeOnConnect(() =>
      this.subscribeTemporaryDocumentCreatedNotification(loggedInUser.userId)
    );
  }

  protected abstract createTemporaryDocumentFromTemplate(): Promise<void>;

  public unsubscribe(): void {
    this.unsubscribeWebsocket?.();
  }

  protected composeWizardSteps(): void {
    this.wizardSteps = composeTemplateWizardSteps(
      this.placeholderLoader.data ?? [],
      this.includeRecipientStep
    );
  }

  public async stepForward(): Promise<void> {
    try {
      const nextStep = this.getStepKeyByDirection(1);
      if (nextStep === TemplateWizardStep.Preview) {
        await this.createTemporaryDocumentFromTemplate();
      }
      runInAction(() => (this.wizardStep = nextStep));
    } catch {
      unexpectedError();
    }
  }

  public stepBack(): void {
    if (this.wizardStep === TemplateWizardStep.ChooseTemplate) {
      return;
    }

    try {
      this.wizardStep = this.getStepKeyByDirection(-1);
    } catch {
      unexpectedError();
    }
  }

  private getStepKeyByDirection(direction: 1 | -1): string {
    const currentStepIndex = this.wizardSteps.findIndex(
      ({key}) => key === this.wizardStep
    );
    if (currentStepIndex === -1) {
      throw Error('Current step cannot be found in wizardSteps array');
    }
    return this.wizardSteps[currentStepIndex + direction].key;
  }

  protected getOverridableTemplatePlaceholders(
    body: GetOverridableTemplatePlaceholdersResponse
  ): void | Promise<void> {
    if (this.placeholderLoader.state === State.Loading) {
      return;
    }
    runInAction(() => {
      this.placeholderLoader.state = State.Loading;
      this.placeholderLoader.data = body.pagedPlaceholders;
      this.composeWizardSteps();
      this.stepForward();
      this.placeholderLoader.state = State.Success;
    });
  }

  protected createTemporaryDocument(
    body: CreateTemporaryDocumentResponse
  ): void {
    runInAction(() => {
      this.temporaryDocumentDataState = State.Loading;
      const {temporaryDocumentId} = body;
      this.temporaryDocumentId = temporaryDocumentId;
      if (this.receivedTemporaryDocumentIds.includes(temporaryDocumentId)) {
        this.receivedTemporaryDocumentIds =
          this.receivedTemporaryDocumentIds.filter(
            (item) => item !== temporaryDocumentId
          );
        this.markTemporaryDocumentAsReady();
      }
    });
  }

  protected async fetchDependablePlaceholderValues(
    params: Parameters<
      typeof documentsBackend.getDependentPlaceholderValues
    >[0]['parameters']
  ): Promise<DefaultValueInResponse[] | void> {
    let defaultValue: DefaultValueInResponse[] | undefined;
    await documentsBackend.getDependentPlaceholderValues({
      parameters: params,
      onSuccess: ({body}) => {
        defaultValue = body;
      },
    });
    return defaultValue ? defaultValue : Promise.resolve();
  }

  protected markTemporaryDocumentAsReady(): void {
    runInAction(() => (this.temporaryDocumentDataState = State.Success));
  }

  protected markTemporaryDocumentAsFailed(): void {
    runInAction(() => (this.temporaryDocumentDataState = State.Error));
  }

  private subscribeTemporaryDocumentCreatedNotification(userId: string): void {
    const response = asyncBackend.topicDocumentManagementUserSpecific(
      {userId},
      (notification) => {
        if (notification) {
          switch (notification.status) {
            case 200:
              runInAction(() => (this.notification = notification));

              switch (notification.body.pushNotificationType) {
                case 'TemporaryDocumentPdfCreationFailedPushNotification':
                  this.markTemporaryDocumentAsFailed();
                  break;
                case 'TemporaryDocumentCreatedPushNotification': {
                  const responseBodyDocument =
                    notification.body as TemporaryDocumentCreatedPushNotification;
                  if (responseBodyDocument.type === 'EMAIL_DOCUMENT') {
                    if (
                      this.temporaryDocumentId ===
                      responseBodyDocument.temporaryDocumentId
                    ) {
                      this.markTemporaryDocumentAsReady();
                    } else {
                      this.receivedTemporaryDocumentIds.push(
                        responseBodyDocument.temporaryDocumentId
                      );
                    }
                  }
                  break;
                }
                case 'TemporaryDocumentPdfCreatedPushNotification': {
                  const responseBody =
                    notification.body as TemporaryDocumentPdfCreatedPushNotification;
                  if (
                    this.temporaryDocumentId ===
                    responseBody.temporaryDocumentId
                  ) {
                    this.markTemporaryDocumentAsReady();
                  } else {
                    this.receivedTemporaryDocumentIds.push(
                      responseBody.temporaryDocumentId
                    );
                  }
                  break;
                }
                case 'UserAddedNewDocumentVersionPushNotification':
                  // no action necessary as this push notification type is not related to templates
                  break;
                default:
                  assertUnreachable(notification.body.pushNotificationType);
              }
              break;
            default:
              assertUnreachable(notification.status);
          }
        }
      }
    );

    this.unsubscribeWebsocket = response.unsubscribe;
  }
}
